import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiTrash2, 
  FiEdit2, 
  FiX, 
  FiCheck,
  FiUser,
  FiSearch,
  FiRefreshCw,
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiXCircle
} from 'react-icons/fi';
import { subscriptionAPI, Subscription, CreateSubscriptionData, authAPI, UserProfile } from '../services/api';
import { API_ENDPOINTS, getToken } from '../config/api';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import './SubscriptionManagement.css';
import './Dashboard.css';

interface SubscriptionManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState({
    email: userEmail || '',
    organization: 'NAATI',
    first_name: ''
  });
  
  // Create form state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSubscriptionData>({
    user_email: '',
    duration_days: 30,
    notes: ''
  });
  const [creating, setCreating] = useState(false);
  
  // User search/autocomplete state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<UserProfile[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const userSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userInputRef = useRef<HTMLInputElement>(null);
  
  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    expires_at: '',
    is_active: true,
    notes: ''
  });
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<string>('all');

  useEffect(() => {
    fetchSubscriptions();
    fetchUserProfile();
  }, []);

  // Handle user search
  useEffect(() => {
    if (userSearchTimeoutRef.current) {
      clearTimeout(userSearchTimeoutRef.current);
    }

    if (userSearchQuery.length >= 2) {
      userSearchTimeoutRef.current = setTimeout(async () => {
        await searchUsers(userSearchQuery);
      }, 300);
    } else {
      setUserSearchResults([]);
      setShowUserDropdown(false);
    }

    return () => {
      if (userSearchTimeoutRef.current) {
        clearTimeout(userSearchTimeoutRef.current);
      }
    };
  }, [userSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userInputRef.current && !userInputRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchUsers = async (query: string) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }

    setSearchingUsers(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_ENDPOINTS.USERS_SEARCH}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const users = await response.json();
        setUserSearchResults(users);
        setShowUserDropdown(true);
      } else {
        setUserSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const selectUser = (user: UserProfile) => {
    const email = user.email ?? '';
    const display = user.email ?? user.username ?? '';
    setCreateForm({ ...createForm, user_email: email });
    setUserSearchQuery(display);
    setShowUserDropdown(false);
  };

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || userEmail || '',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || ''
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await subscriptionAPI.listSubscriptions();
      setSubscriptions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch subscriptions');
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.user_email) {
      setError('Please enter user email');
      return;
    }
    if (createForm.duration_days < 1) {
      setError('Duration must be at least 1 day');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const newSubscription = await subscriptionAPI.createSubscription(createForm);
      setSubscriptions([newSubscription, ...subscriptions]);
      setSuccess('Subscription created successfully');
      setShowCreateModal(false);
      setCreateForm({
        user_email: '',
        duration_days: 30,
        notes: ''
      });
      setUserSearchQuery('');
      setUserSearchResults([]);
      setShowUserDropdown(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription');
      console.error('Error creating subscription:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (subscription: Subscription) => {
    setEditingId(subscription.id);
    setEditForm({
      expires_at: subscription.expires_at.split('T')[0], // Format date for input
      is_active: subscription.is_active,
      notes: subscription.notes || ''
    });
  };

  const handleUpdate = async () => {
    if (!editingId) return;

    setLoading(true);
    setError(null);
    try {
      const updated = await subscriptionAPI.updateSubscription(editingId, editForm);
      setSubscriptions(subscriptions.map(s => s.id === editingId ? updated : s));
      setSuccess('Subscription updated successfully');
      setEditingId(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update subscription');
      console.error('Error updating subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await subscriptionAPI.deleteSubscription(id);
      setSubscriptions(subscriptions.filter(s => s.id !== id));
      setSuccess('Subscription deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete subscription');
      console.error('Error deleting subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  // Filter subscriptions
  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = 
      sub.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sub.user_name && sub.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sub.notes && sub.notes.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = 
      filterActive === 'all' ||
      (filterActive === 'active' && sub.is_valid) ||
      (filterActive === 'expired' && !sub.is_valid);

    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={handleLogout}
        />
        
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              className="dashboard-welcome"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div>
                <h1 className="dashboard-title">Subscription Management</h1>
                <p className="dashboard-subtitle">Manage user subscriptions and access</p>
              </div>
              <motion.button
                className="create-btn"
                onClick={() => setShowCreateModal(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 18 })}
                <span>Add Subscription</span>
              </motion.button>
            </motion.div>

            {/* Messages */}
            {error && (
              <motion.div
                className="message error"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setError(null)}
              >
                {error}
              </motion.div>
            )}
            {success && (
              <motion.div
                className="message success"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setSuccess(null)}
              >
                {success}
              </motion.div>
            )}

            {/* Filters */}
            <div className="filters-section">
              <div className="search-box">
                {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                <input
                  type="text"
                  placeholder="Search by email, name, or notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                >
                  <option value="all">All Subscriptions</option>
                  <option value="active">Active Only</option>
                  <option value="expired">Expired Only</option>
                </select>
              </div>
              <button
                className="refresh-btn"
                onClick={fetchSubscriptions}
                disabled={loading}
              >
                {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 18 })}
              </button>
            </div>

            {/* Subscriptions Table */}
            <div className="table-container">
              {loading && subscriptions.length === 0 ? (
                <div className="loading">Loading subscriptions...</div>
              ) : filteredSubscriptions.length === 0 ? (
                <div className="empty-state">
                  <p>No subscriptions found</p>
                </div>
              ) : (
                <table className="subscriptions-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Start Date</th>
                      <th>Expires At</th>
                      <th>Days Remaining</th>
                      <th>Status</th>
                      <th>Created By</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubscriptions.map((subscription) => (
                      <motion.tr
                        key={subscription.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={editingId === subscription.id ? 'editing' : ''}
                      >
                        {editingId === subscription.id ? (
                          <>
                            <td>{subscription.user_email}</td>
                            <td>{formatDate(subscription.start_date)}</td>
                            <td>
                              <input
                                type="date"
                                value={editForm.expires_at}
                                onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })}
                              />
                            </td>
                            <td>{subscription.days_remaining}</td>
                            <td>
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={editForm.is_active}
                                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                />
                                <span className="toggle-slider"></span>
                              </label>
                            </td>
                            <td>{subscription.created_by_email || 'N/A'}</td>
                            <td>
                              <input
                                type="text"
                                value={editForm.notes || ''}
                                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                placeholder="Notes..."
                              />
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="action-btn save"
                                  onClick={handleUpdate}
                                  disabled={loading}
                                >
                                  {React.createElement(FiCheck as React.ComponentType<{ size?: number }>, { size: 16 })}
                                </button>
                                <button
                                  className="action-btn cancel"
                                  onClick={() => setEditingId(null)}
                                >
                                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 16 })}
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <div className="user-info">
                                <span className="user-email">{subscription.user_email}</span>
                                {subscription.user_name && subscription.user_name !== subscription.user_email && (
                                  <span className="user-name">{subscription.user_name}</span>
                                )}
                              </div>
                            </td>
                            <td>{formatDate(subscription.start_date)}</td>
                            <td>{formatDate(subscription.expires_at)}</td>
                            <td>
                              <span className={`days-remaining ${subscription.days_remaining <= 7 ? 'warning' : ''}`}>
                                {subscription.days_remaining} days
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${subscription.is_valid ? 'active' : 'expired'}`}>
                                {subscription.is_valid ? (
                                  <>
                                    {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 14 })}
                                    Active
                                  </>
                                ) : (
                                  <>
                                    {React.createElement(FiXCircle as React.ComponentType<{ size?: number }>, { size: 14 })}
                                    Expired
                                  </>
                                )}
                              </span>
                            </td>
                            <td>{subscription.created_by_email || 'N/A'}</td>
                            <td className="notes-cell">{subscription.notes || '-'}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="action-btn edit"
                                  onClick={() => handleEdit(subscription)}
                                  title="Edit"
                                >
                                  {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                                </button>
                                <button
                                  className="action-btn delete"
                                  onClick={() => handleDelete(subscription.id)}
                                  title="Delete"
                                >
                                  {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Create New Subscription</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowCreateModal(false)}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 20 })}
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>User Email *</label>
                  <div className="user-search-wrapper" ref={userInputRef}>
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        setCreateForm({ ...createForm, user_email: e.target.value });
                      }}
                      onFocus={() => {
                        if (userSearchResults.length > 0) {
                          setShowUserDropdown(true);
                        }
                      }}
                      placeholder="Search user by email..."
                      required
                      autoComplete="off"
                    />
                    {searchingUsers && (
                      <div className="search-loading">
                        {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, { size: 16, className: 'spinner' })}
                      </div>
                    )}
                    {showUserDropdown && userSearchResults.length > 0 && (
                      <div className="user-dropdown">
                        {userSearchResults.map((user) => (
                          <div
                            key={user.id}
                            className="user-dropdown-item"
                            onClick={() => selectUser(user)}
                          >
                            <div className="user-dropdown-email">
                              {user.email ?? user.username ?? '—'}
                            </div>
                            {user.first_name || user.last_name ? (
                              <div className="user-dropdown-name">
                                {user.first_name} {user.last_name}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                    {showUserDropdown && userSearchResults.length === 0 && userSearchQuery.length >= 2 && !searchingUsers && (
                      <div className="user-dropdown">
                        <div className="user-dropdown-item no-results">No users found</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Duration (Days) *</label>
                  <input
                    type="number"
                    min="1"
                    max="3650"
                    value={createForm.duration_days}
                    onChange={(e) => setCreateForm({ ...createForm, duration_days: parseInt(e.target.value) || 30 })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                    placeholder="Optional notes about this subscription..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreate}
                  disabled={creating || !createForm.user_email}
                >
                  {creating ? 'Creating...' : 'Create Subscription'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionManagement;

